// Local SVG Generator for End-to-End Cables
// This service generates SVG images locally without needing the EC2 server

// Import the static compatibility matrix directly
import { compatibilityMatrix } from './compatibilityMatrix.js';

/**
 * Encode each path segment so filenames with spaces/parens load in SVG <image> and canvas
 */
const encodePublicImageUrl = (fullUrl) => {
  if (!fullUrl || typeof fullUrl !== 'string') return fullUrl;
  try {
    const u = new URL(fullUrl);
    const segments = u.pathname.split('/').filter(Boolean);
    u.pathname =
      '/' +
      segments
        .map((seg) => {
          try {
            return encodeURIComponent(decodeURIComponent(seg));
          } catch {
            return encodeURIComponent(seg);
          }
        })
        .join('/');
    return u.href;
  } catch {
    return fullUrl.replace(/ /g, '%20');
  }
};

/**
 * Convert relative image paths to absolute URLs
 * Supports both local testing and EC2 production environments
 * @param {string} imagePath - Relative or absolute image path
 * @param {string} baseUrl - Base URL for the current environment
 * @returns {string} - Absolute URL
 */

const makeAbsoluteUrl = (imagePath, baseUrl = null) => {
  if (!imagePath) return null;

  let full;
  if (imagePath.startsWith('http')) {
    full = imagePath;
  } else {
    let origin;
    if (baseUrl) {
      origin = baseUrl;
    } else if (typeof window !== 'undefined' && window.location) {
      console.log('🌐 [LOCAL] Browser environment detected, using full image URL');
      origin = 'https://nlfx-cable.luknai.com';
    } else {
      origin = 'https://nlfx-cable.luknai.com';
    }

    console.log('🌐 [LOCAL] makeAbsoluteUrl:', { imagePath, baseUrl, origin });

    if (imagePath.startsWith('/')) {
      full = `${origin}${imagePath}`;
    } else {
      full = `${origin}/images/${imagePath}`;
    }
  }

  return encodePublicImageUrl(full);
};

/**
 * Wrap text into multiple lines with aggressive overflow prevention
 * @param {string} text - Text to wrap
 * @param {number} maxChars - Maximum characters per line
 * @returns {Array<string>} - Array of lines
 */
const wrapText = (text, maxChars = 15) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';
  
  words.forEach(word => {
    // If adding this word would exceed the limit
    if ((currentLine + ' ' + word).trim().length > maxChars) {
      // If current line has content, save it and start new line
      if (currentLine.trim()) {
        lines.push(currentLine.trim());
        currentLine = word;
      } else {
        // If current line is empty, force the word to fit (for very long words)
        // But break very long words if they exceed the limit
        if (word.length > maxChars) {
          // Break long words at character boundaries
          for (let i = 0; i < word.length; i += maxChars) {
            lines.push(word.slice(i, i + maxChars));
          }
        } else {
          lines.push(word);
        }
        currentLine = '';
      }
    } else {
      // Add word to current line
      currentLine = currentLine ? currentLine + ' ' + word : word;
    }
  });
  
  // Add the last line if it has content
  if (currentLine.trim()) {
    lines.push(currentLine.trim());
  }
  
  return lines;
};

const escapeSvgText = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Match server `wrapConnectorLabelLines` — wider lines than old max 15 chars */
const wrapConnectorLabelLines = (text, maxChars = 24) => {
  const words = String(text || '')
    .split(/\s+/)
    .filter(Boolean);
  const lines = [];
  let currentLine = '';
  words.forEach((word) => {
    const tryLine = currentLine ? `${currentLine} ${word}` : word;
    if (tryLine.length <= maxChars) {
      currentLine = tryLine;
    } else {
      if (currentLine) lines.push(currentLine);
      if (word.length <= maxChars) {
        currentLine = word;
      } else {
        for (let i = 0; i < word.length; i += maxChars) {
          lines.push(word.slice(i, i + maxChars));
        }
        currentLine = '';
      }
    }
  });
  if (currentLine) lines.push(currentLine);
  return lines.length ? lines : [''];
};

/**
 * Get connector images - enhanced to handle multiple connector data formats
 * @param {Object} connector - Connector object
 * @param {string} baseUrl - Base URL for image resolution
 * @returns {string|null} - Absolute URL to connector image or null
 */
const getConnectorImage = (connector, baseUrl) => {
  if (!connector) return null;
  
  // First, try to get the image from the compatibility matrix
  // The connector object has a 'connector' property that contains the ID
  let connectorId = connector.connector || connector.id;
  
  console.log('🔍 [LOCAL] getConnectorImage called with:', { connectorId, baseUrl });
  
  if (connectorId) {
    try {
      // Access the static compatibility matrix directly
      if (compatibilityMatrix[connectorId] && compatibilityMatrix[connectorId].image) {
        const imagePath = compatibilityMatrix[connectorId].image;
        console.log('✅ [LOCAL] Found image in matrix:', { connectorId, imagePath });
        if (imagePath && imagePath !== '/images/coming_soon.png') {
          const absoluteUrl = makeAbsoluteUrl(imagePath, baseUrl);
          console.log('🔗 [LOCAL] Generated absolute URL:', absoluteUrl);
          return absoluteUrl;
        }
      } else {
        console.log('⚠️ [LOCAL] No image found in matrix for:', connectorId);
      }
    } catch (error) {
      console.warn('⚠️ [LOCAL] Could not access compatibility matrix for image lookup:', error.message);
    }
  }
  
  // Fallback to direct image properties
  const imagePath = connector.image || 
                   connector.connectorImage || 
                   connector.connector?.image;
  
  if (imagePath) {
    console.log('🔄 [LOCAL] Using fallback image path:', imagePath);
    return makeAbsoluteUrl(imagePath, baseUrl);
  }
  
  console.log('❌ [LOCAL] No image found for connector:', connectorId);
  // If no image found, return null to show placeholder
  return null;
};

/**
 * Generate Base/Standard cable SVG for simple endA-endB connections
 * @param {Object} config - Configuration object with connectors, wireType, length
 * @param {Object} config.connectors - Connector objects for endA and endB
 * @param {string} config.wireType - Type of wire (e.g., 'PSM', 'RG6')
 * @param {number} config.length - Length of the cable
 * @param {string} config.unit - Unit of measurement (e.g., 'ft', 'm')
 * @param {string} config.sku - SKU identifier to display on a new line in the wire description box
 * @param {string} config.baseUrl - Base URL for image resolution (for EC2 environment)
 * @returns {string} - SVG markup aligned with BaseConnectorSelector (green frames, NLFX red arrow)
 */
const { resolveLengthFeetForImage } = require('../utils/cableLengthUtils');

export function generateEndToEndSVG(config) {
  const { connectors = {}, wireType = 'PSM', unit = 'ft', sku = '', baseUrl = null } = config;
  const length = resolveLengthFeetForImage(config);

  const svgW = 720;
  const svgH = 320;
  const pad = 40;
  const frame = 80;
  const imgSz = 70;
  const inset = (frame - imgSz) / 2;
  const startX = pad + frame / 2;
  const endX = svgW - pad - frame / 2;
  const centerY = 120;
  const mid = (startX + endX) / 2;
  const arrowW = 12;
  const arrowH = 8;
  const wireH = 6;
  const leftSegX0 = startX + frame / 2;
  const leftSegW = Math.max(28, mid - arrowW / 2 - leftSegX0);
  const rightSegX0 = mid + arrowW / 2;
  const rightSegW = Math.max(28, endX - frame / 2 - rightSegX0);

  const endAImage = getConnectorImage(connectors.endA, baseUrl);
  const endBImage = getConnectorImage(connectors.endB, baseUrl);

  console.log('🖼️ [LOCAL] Connector images resolved:', {
    endA: { id: connectors.endA?.connector, image: endAImage },
    endB: { id: connectors.endB?.connector, image: endBImage }
  });

  const getConnectorName = (connector) => {
    if (connector?.description) return connector.description;
    if (connector?.name) return connector.name;
    if (connector?.family && connector?.gender) {
      return `${connector.family} ${connector.gender}`;
    }
    if (connector?.family) return connector.family;
    if (connector?.skuCode) return connector.skuCode;
    return 'Unknown Connector';
  };

  const lenNum = Number(length);
  const lenStr = Number.isFinite(lenNum) ? lenNum : 10;
  const wireInfo = `${lenStr} ${unit} ${wireType} Wire`;
  const cleanSku = sku
    .trim()
    .replace(/_+\d{8}_+\d{6,}$/, '')
    .replace(/--\d{8}-\d{6,}$/, '')
    .trim();

  const labelMax = 24;
  const linesA = wrapConnectorLabelLines(getConnectorName(connectors.endA), labelMax);
  const linesB = wrapConnectorLabelLines(getConnectorName(connectors.endB), labelMax);

  const fontStack =
    "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  const labelTop = centerY + frame / 2 + 16;
  const lineHeightEm = '1.2em';

  const tspansFor = (lines, cx) =>
    lines
      .map(
        (line, i) =>
          `<tspan x="${cx}" dy="${i === 0 ? '0' : lineHeightEm}">${escapeSvgText(line)}</tspan>`
      )
      .join('');

  const panelX = 24;
  const panelY = 28;
  const panelW = svgW - 48;
  const panelH = 200;

  const svgContent = `
    <svg width="${svgW}" height="${svgH}" viewBox="0 0 ${svgW} ${svgH}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="nlfxWireGradLocal" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stop-color="#4a4a4a"/>
          <stop offset="50%" stop-color="#666666"/>
          <stop offset="100%" stop-color="#4a4a4a"/>
        </linearGradient>
        <filter id="nlfxWireShadeLocal" x="-30%" y="-80%" width="160%" height="260%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000000" flood-opacity="0.2"/>
        </filter>
      </defs>

      <rect width="${svgW}" height="${svgH}" fill="#fafafa"/>

      <rect x="${panelX}" y="${panelY}" width="${panelW}" height="${panelH}" rx="8"
            fill="#f8f9fa" stroke="#e8e8e8" stroke-width="1"/>

      <rect x="${startX - frame / 2}" y="${centerY - frame / 2}" width="${frame}" height="${frame}" rx="8"
            fill="#e8f5e8" stroke="#4caf50" stroke-width="2"/>
      ${
        endAImage
          ? `<image href="${endAImage}" x="${startX - frame / 2 + inset}" y="${centerY - frame / 2 + inset}" width="${imgSz}" height="${imgSz}" preserveAspectRatio="xMidYMid meet"/>`
          : `<text x="${startX}" y="${centerY + 6}" text-anchor="middle" font-family="${fontStack}" font-size="28" font-weight="700" fill="#757575">A</text>`
      }

      <rect x="${leftSegX0}" y="${centerY - wireH / 2}" width="${leftSegW}" height="${wireH}" rx="3"
            fill="url(#nlfxWireGradLocal)" filter="url(#nlfxWireShadeLocal)"/>
      <polygon points="${mid - arrowW / 2},${centerY - arrowH / 2} ${mid + arrowW / 2},${centerY} ${mid - arrowW / 2},${centerY + arrowH / 2}"
               fill="#b32b2b"/>
      <rect x="${rightSegX0}" y="${centerY - wireH / 2}" width="${rightSegW}" height="${wireH}" rx="3"
            fill="url(#nlfxWireGradLocal)" filter="url(#nlfxWireShadeLocal)"/>

      <rect x="${endX - frame / 2}" y="${centerY - frame / 2}" width="${frame}" height="${frame}" rx="8"
            fill="#e8f5e8" stroke="#4caf50" stroke-width="2"/>
      ${
        endBImage
          ? `<image href="${endBImage}" x="${endX - frame / 2 + inset}" y="${centerY - frame / 2 + inset}" width="${imgSz}" height="${imgSz}" preserveAspectRatio="xMidYMid meet"/>`
          : `<text x="${endX}" y="${centerY + 6}" text-anchor="middle" font-family="${fontStack}" font-size="28" font-weight="700" fill="#757575">B</text>`
      }

      <text x="${startX}" y="${labelTop}" text-anchor="middle" font-family="${fontStack}" font-size="13" font-weight="500" fill="#333333">
        ${tspansFor(linesA, startX)}
      </text>
      <text x="${mid}" y="${labelTop}" text-anchor="middle" font-family="${fontStack}" font-size="12" fill="#666666">
        <tspan x="${mid}" dy="0">${escapeSvgText(wireInfo)}</tspan>
        ${
          cleanSku
            ? `<tspan x="${mid}" dy="1.15em">${escapeSvgText(cleanSku)}</tspan>`
            : ''
        }
      </text>
      <text x="${endX}" y="${labelTop}" text-anchor="middle" font-family="${fontStack}" font-size="13" font-weight="500" fill="#333333">
        ${tspansFor(linesB, endX)}
      </text>
    </svg>
  `;

  console.log('🔍 [LOCAL] SVG Image Elements:', {
    endA: endAImage ? `<image href="${endAImage}" ... />` : 'PLACEHOLDER',
    endB: endBImage ? `<image href="${endBImage}" ... />` : 'PLACEHOLDER'
  });

  return svgContent;
}

/**
 * Capture image from a specific DOM element using Puppeteer (server-side)
 * This is the same approach used in WyeConnectorSelection.js and App.js
 * Much more reliable than html2canvas as it runs in a real browser environment
 * @param {string} selector - CSS selector or XPath selector for the element to capture
 * @param {Object} options - Capture options
 * @returns {Promise<string>} - S3 URL to the captured image
 */
export async function captureElementAsImage(selector, options = {}) {
  return new Promise(async (resolve, reject) => {
    try {
      console.log('🔄 [CAPTURE] Starting html2canvas-based DOM element capture for selector:', selector);
      
      // Find the element to capture - handle both CSS and XPath selectors
      let element;
      if (selector.startsWith('//')) {
        // XPath selector
        console.log('🎯 [CAPTURE] Using XPath selector');
        const result = document.evaluate(
          selector, 
          document, 
          null, 
          XPathResult.FIRST_ORDERED_NODE_TYPE, 
          null
        );
        element = result.singleNodeValue;
      } else {
        // CSS selector
        console.log('🎯 [CAPTURE] Using CSS selector');
        element = document.querySelector(selector);
      }
      
      if (!element) {
        throw new Error(`Element not found with selector: ${selector}`);
      }
      
      console.log('✅ [CAPTURE] Found target element:', {
        tagName: element.tagName,
        className: element.className,
        id: element.id,
        dimensions: {
          width: element.offsetWidth,
          height: element.offsetHeight
        }
      });
      
      // Generate unique filename
      const timestamp = Date.now();
      const outputFileName = `cable-config-${timestamp}.png`;
      
      // Use html2canvas to capture the live rendered element with all styling
      console.log(`📸 [CAPTURE] Using html2canvas to capture live rendered element...`);
      
      // Import html2canvas dynamically
      const html2canvas = (await import('html2canvas')).default;
      
      // Capture the element directly with html2canvas - this preserves all Material-UI styling!
      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff', // Clean white background
        scale: 2, // High DPI for crisp images
        logging: false,
        useCORS: true,
        allowTaint: true,
        width: element.offsetWidth,
        height: element.offsetHeight,
        onclone: (clonedDoc) => {
          // Ensure all styles are preserved in the clone
          const clonedElement = clonedDoc.querySelector(selector);
          if (clonedElement) {
            // Force visibility and proper positioning
            clonedElement.style.visibility = 'visible';
            clonedElement.style.opacity = '1';
            clonedElement.style.position = 'relative';
            clonedElement.style.transform = 'none';
            clonedElement.style.display = 'block';
            
            // Ensure all child elements are visible
            const allChildren = clonedElement.querySelectorAll('*');
            allChildren.forEach(child => {
              child.style.visibility = 'visible';
              child.style.opacity = '1';
            });
          }
        }
      });
      
      // Crop 20% from each side and 10% from bottom for better focus on the cable configuration
      console.log(`✂️ [CAPTURE] Cropping 20% from each side and 10% from bottom for better composition...`);
      
      const cropCanvas = document.createElement('canvas');
      const cropCtx = cropCanvas.getContext('2d');
      
      // Calculate crop dimensions (remove 20% from each side = 60% width, remove 10% from bottom = 90% height)
      const sideCropPercentage = 0.2;
      const bottomCropPercentage = 0.1;
      const cropX = canvas.width * sideCropPercentage;
      const cropY = 0; // Start from top
      const cropWidth = canvas.width * (1 - 2 * sideCropPercentage); // 60% of original width
      const cropHeight = canvas.height * (1 - bottomCropPercentage); // 90% of original height
      
      // Set the crop canvas size
      cropCanvas.width = cropWidth;
      cropCanvas.height = cropHeight;
      
      // Draw the cropped portion
      cropCtx.drawImage(
        canvas,
        cropX, cropY, cropWidth, cropHeight, // Source: crop from 20% in, 10% from bottom
        0, 0, cropWidth, cropHeight          // Destination: fill the crop canvas
      );
      
      // Convert cropped canvas to base64 PNG
      const imageData = cropCanvas.toDataURL('image/png');
      console.log(`✅ [CAPTURE] html2canvas capture and crop successful, final image data length: ${imageData.length}`);
      
      // Upload to S3 using the correct endpoint
      console.log(`📡 [CAPTURE] Uploading to S3 via /api/upload-diagram...`);
      const response = await fetch('https://nlfx-cable.luknai.com/api/upload-diagram', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sku: options.outputFileName || `cable-config-${Date.now()}`,
          imageData: imageData
        })
      });
      
      console.log('📡 [CAPTURE] Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('📡 [CAPTURE] Error response body:', errorText);
        throw new Error(`Server responded with ${response.status}: ${response.statusText} - ${errorText}`);
      }
      
      const result = await response.json();
      
      if (result.url) {
        console.log('✅ [CAPTURE] html2canvas capture and upload successful:', result.url);
        resolve(result.url); // Return the S3 URL
      } else {
        throw new Error(result.error || 'Unknown error from server');
      }
      
    } catch (error) {
      console.error('❌ [CAPTURE] Error during html2canvas capture:', error);
      reject(error);
    }
  });
}

/**
 * Capture the cable configuration display as an image using Puppeteer
 * This targets the specific DIV that contains your cable visualization
 * Uses the same server-side Puppeteer approach as WyeConnectorSelection.js
 * @param {Object} options - Capture options
 * @returns {Promise<string>} - S3 URL to the captured image
 */
export async function captureCableConfiguration(options = {}) {
  // Target the specific element containing the complete cable visualization
  // Using the exact selector path you provided
  const cssSelector = '#root > div > div > div.MuiBox-root.css-1olexlt > div.MuiBox-root.css-178yklu > div > div.MuiBox-root.css-ldo4d5';
  
  console.log('🎯 [CABLE-CAPTURE] Targeting cable configuration element with CSS selector:', cssSelector);
  
  // First, let's find and inspect the element to make sure we're targeting the right thing
  const targetElement = document.querySelector(cssSelector);
  
  if (targetElement) {
    console.log('🔍 [CABLE-CAPTURE] Found target element:', {
      tagName: targetElement.tagName,
      className: targetElement.className,
      id: targetElement.id,
      children: targetElement.children.length,
      innerHTML: targetElement.innerHTML.substring(0, 200) + '...'
    });
    
    // Log all child elements to help debug
    Array.from(targetElement.children).forEach((child, index) => {
      console.log(`🔍 [CABLE-CAPTURE] Child ${index}:`, {
        tagName: child.tagName,
        className: child.className,
        children: child.children.length,
        textContent: child.textContent?.substring(0, 50) + '...'
      });
    });
  } else {
    console.warn('⚠️ [CABLE-CAPTURE] Target element not found with CSS selector');
    
    // Try alternative selectors as fallback
    const alternatives = [
      '.MuiPaper-root.MuiPaper-elevation.MuiPaper-rounded.MuiPaper-elevation1.css-t5926t', // Specific Paper with class
      '.css-t5926t', // Just the specific CSS class
      '.MuiBox-root.css-ldo4d5', // The outer container
      '.MuiPaper-root.MuiPaper-elevation.MuiPaper-rounded.MuiPaper-elevation1',
      '.MuiPaper-root'
    ];
    
    for (const altSelector of alternatives) {
      const altElement = document.querySelector(altSelector);
      if (altElement) {
        console.log(`🔍 [CABLE-CAPTURE] Found alternative element with selector: ${altSelector}`, {
          tagName: altElement.tagName,
          className: altElement.className,
          children: altElement.children.length
        });
        break;
      }
    }
  }
  
  try {
    // Use the new Puppeteer-based capture function with CSS selector
    const s3Url = await captureElementAsImage(cssSelector, {
      ...options,
      captureType: 'base-connector',
      contentType: 'html'
    });
    
    console.log('✅ [CABLE-CAPTURE] Cable configuration captured successfully via Puppeteer');
    console.log('🔗 [CABLE-CAPTURE] S3 URL:', s3Url);
    
    return s3Url;
    
  } catch (error) {
    console.error('❌ [CABLE-CAPTURE] Failed to capture cable configuration via Puppeteer:', error);
    throw error;
  }
}

/**
 * Convert SVG to PNG using canvas
 * @param {string} svgContent - The SVG markup
 * @returns {Promise<string>} - Base64 encoded PNG data
 */
export async function convertSVGToPNG(svgContent) {
  return new Promise((resolve, reject) => {
    try {
      const logicalW = 720;
      const logicalH = 320;
      const scale = 2;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = logicalW * scale;
      canvas.height = logicalH * scale;
      ctx.scale(scale, scale);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      const img = new Image();
      const svgBlob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      img.onload = () => {
        try {
          ctx.drawImage(img, 0, 0, logicalW, logicalH);
          
          // Convert to PNG
          const pngData = canvas.toDataURL('image/png');
          
          // Clean up
          URL.revokeObjectURL(url);
          
          resolve(pngData);
        } catch (error) {
          URL.revokeObjectURL(url);
          reject(error);
        }
      };
      
      img.onerror = (error) => {
        URL.revokeObjectURL(url);
        reject(error);
      };
      
      img.src = url;
      
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Generate and upload end-to-end cable image
 * @param {Object} config - Configuration object
 * @returns {Promise<Object>} - Result with success status and image data
 */
export async function generateEndToEndImage(config) {
  try {
    console.log('🖼️ [LOCAL] Generating end-to-end cable image with config:', config);
    
    // Try Puppeteer capture first (most reliable)
    try {
      console.log('🎯 [LOCAL] Attempting Puppeteer capture of cable configuration...');
      const s3Url = await captureCableConfiguration();
      
      console.log('✅ [LOCAL] Puppeteer capture successful, S3 URL:', s3Url);
      
      return {
        success: true,
        imageData: s3Url, // Now contains S3 URL instead of base64 PNG data
        svgData: null, // No SVG data when using Puppeteer capture
        metadata: {
          style: 'end-to-end',
          timestamp: Date.now(),
          method: 'puppeteer-capture',
          captureMethod: 'server-side-puppeteer',
          s3Url: s3Url
        }
      };
      
    } catch (captureError) {
      console.warn('⚠️ [LOCAL] Puppeteer capture failed, falling back to SVG generation:', captureError.message);
      
      // Fallback to SVG generation
      const svgContent = generateEndToEndSVG(config);
      
      if (!svgContent) {
        throw new Error('Failed to generate SVG content');
      }
      
      console.log('✅ [LOCAL] SVG generated successfully, length:', svgContent.length);
      
      // Convert to PNG
      const pngData = await convertSVGToPNG(svgContent);
      
      if (!pngData) {
        throw new Error('Failed to convert SVG to PNG');
      }
      
      console.log('✅ [LOCAL] PNG conversion successful (fallback method)');
      
      return {
        success: true,
        imageData: pngData,
        svgData: svgContent,
        metadata: {
          style: 'end-to-end',
          timestamp: Date.now(),
          method: 'local-svg-generation',
          fallback: true,
          fallbackReason: captureError.message
        }
      };
    }
    
  } catch (error) {
    console.error('❌ [LOCAL] Error generating end-to-end cable image:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Generate end-to-end cable image for EC2 environment with proper image handling
 * Downloads connector images and embeds them in the generated PNG
 * @param {Object} config - Configuration object
 * @param {string} config.baseUrl - Base URL for the EC2 environment
 * @returns {Promise<Object>} - Result with success status and image data
 */
export async function generateEndToEndImageEC2(config) {
  try {
    console.log('🖼️ [EC2] Generating end-to-end cable image with config:', config);
    
    // Validate base URL for EC2 environment
    if (!config.baseUrl) {
      throw new Error('EC2 environment requires baseUrl for image resolution');
    }
    
    // Generate SVG with EC2 base URL
    const svgContent = generateEndToEndSVG(config);
    
    if (!svgContent) {
      throw new Error('Failed to generate SVG content');
    }
    
    console.log('✅ [EC2] SVG generated successfully, length:', svgContent.length);
    
    // Convert to PNG (this will include the properly resolved images)
    const pngData = await convertSVGToPNG(svgContent);
    
    if (!pngData) {
      throw new Error('Failed to convert SVG to PNG');
    }
    
    console.log('✅ [EC2] PNG conversion successful with embedded images');
    
    return {
      success: true,
      imageData: pngData,
      svgData: svgContent,
      metadata: {
        style: 'end-to-end',
        timestamp: Date.now(),
        method: 'ec2-svg-generation',
        baseUrl: config.baseUrl
      }
    };
    
  } catch (error) {
    console.error('❌ [EC2] Error generating end-to-end cable image:', error);
    return {
      success: false,
      error: error.message
    };
  }
}